import { BaseClient } from './baseClient';

/** Per-stage org-wide RFQ counts for the pipeline band (server-derived). */
export interface DashboardPipelineCounts {
    uploadBom: number;
    sourcingCriteria: number;
    supplierDiscovery: number;
    supplierOutreach: number;
    quotesProposals: number;
}

/**
 * Org-wide dashboard rollup from `GET /trp/dashboard/summary` — aggregated
 * server-side over the full collections, so counts stay exact for orgs with
 * more RFQs than one list page. Metrics the data cannot truthfully support
 * arrive as `null`, each named with its reason in `notes`.
 */
export interface DashboardSummary {
    activeTotal: number;
    stagesInPlay: number;
    awaitingReview: number;
    oldestWaitingDays: number | null;
    /** Active RFQs whose technical sub-steps sit with a named end user (handoff PENDING). */
    pendingOnEndUser: number;
    suppliersEngaged: number | null;
    suppliersRespondedThisWeek: number | null;
    avgCycleDays: number | null;
    /** Signed % change vs the previous 90-day window; negative = faster. */
    avgCycleDeltaPct: number | null;
    completedCount: number;
    awardedThisMonth: number;
    /**
     * The signed-in user's own RFQs (`createdBy`), the one caller-scoped pair
     * in an otherwise org-wide payload. `myRfqTotal` counts every status —
     * "have you ever run one", which is what the new-person welcome asks;
     * `myActiveTotal` counts what is still in flight, which is what the
     * active-only table could actually show.
     */
    myRfqTotal: number;
    myActiveTotal: number;
    pipeline: DashboardPipelineCounts;
    notes: string[];
}

interface DashboardSummaryResponse {
    success: boolean;
    data: DashboardSummary;
}

class DashboardClient extends BaseClient {
    async getSummary(): Promise<DashboardSummaryResponse> {
        const response = await this.api.get<DashboardSummaryResponse>('/trp/dashboard/summary');
        return response.data;
    }
}

export const dashboardClient = new DashboardClient();
